import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FolderSimple } from "@phosphor-icons/react";
import { useLanguage } from "@/contexts/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function GalleryPage() {
  const { t } = useLanguage();
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/albums`)
      .then((r) => r.json())
      .then((data) => setAlbums(Array.isArray(data) ? data : []))
      .catch(() => setAlbums([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="gallery-page">
      {/* Page Header */}
      <section
        className="py-20 md:py-28 border-b"
        style={{ backgroundColor: '#E8EDE9', borderColor: '#D3DCD6' }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs uppercase tracking-[0.2em] font-medium mb-4" style={{ color: '#B85C38' }}>
            {t("gallery_tagline")}
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium" style={{ color: '#1C211F' }}>
            {t("gallery_heading")}
          </h1>
        </div>
      </section>

      {/* Albums Grid */}
      <section className="py-24 md:py-32" style={{ backgroundColor: '#F7F5F0' }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="flex items-center justify-center py-24">
              <div className="w-7 h-7 border-2 rounded-full animate-spin" style={{ borderColor: '#1A5C37', borderTopColor: 'transparent' }} />
            </div>
          ) : albums.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: 'rgba(26,92,55,0.08)' }}>
                <FolderSimple size={32} weight="duotone" style={{ color: '#1A5C37' }} />
              </div>
              <p className="text-sm font-medium" style={{ color: '#1C211F' }}>{t("no_images")}</p>
              <p className="text-xs mt-1" style={{ color: '#4A5450' }}>{t("no_images_sub")}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {albums.map((a) => (
                <Link
                  key={a.id}
                  to={`/gallery/${a.id}`}
                  data-testid={`album-card-${a.id}`}
                  className="group rounded-xl overflow-hidden border block transition-all duration-200"
                  style={{ borderColor: 'rgba(26,92,55,0.1)', backgroundColor: '#fff' }}
                  onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 8px 28px rgba(26,92,55,0.12)'; e.currentTarget.style.borderColor = 'rgba(26,92,55,0.3)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = 'rgba(26,92,55,0.1)'; }}
                >
                  <div className="relative overflow-hidden aspect-[4/3]">
                    <img
                      src={`${BACKEND_URL}${a.cover_url}`}
                      alt={a.name}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                    <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(26,33,31,0.55) 0%, transparent 55%)' }} />
                    <span
                      className="absolute top-3 right-3 text-[11px] font-medium px-2.5 py-1 rounded-full"
                      style={{ backgroundColor: 'rgba(247,245,240,0.92)', color: '#1A5C37' }}
                    >
                      {a.image_count} {a.image_count === 1 ? 'image' : 'images'}
                    </span>
                  </div>
                  <div className="px-5 py-4">
                    <h3 className="font-heading text-lg font-medium truncate" style={{ color: '#1C211F' }}>{a.name}</h3>
                    <p className="text-xs mt-1" style={{ color: '#1A5C37' }}>View album →</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
